import { camelCase } from 'lodash-es'
import { $logger } from '@/boot/logger'
import { $flagsmith, defaultFlags } from '@/boot/flagsmith'

export const useFeatureFlagStore = defineStore('featureFlag', () => {
  const flagsAreInitialized = ref(false)
  // Create reactive state for feature flags with default values
  const featureFlags = reactive({})

  /**
   * Update feature flags in the reactive state
   * This function is called whenever flag changes are detected.
   */
  function initFeatureFlags() {
    // Always overlay live flags ON TOP of defaultFlags. Before flagsmith finishes
    // initializing, getAllFlags() returns an empty-but-truthy {} — so the old
    // `getAllFlags() || defaultFlags` quietly skipped the defaults and left the
    // store missing keys like blacklistSlugs. A beforeEach router guard
    // (isBlacklistPath) then read `featureFlags.blacklistSlugs.value` on an
    // undefined flag and threw while the router was starting, aborting router
    // startup and leaving a blank page (the DEV-u30r cold-run flake). Spreading
    // defaults first guarantees every default key is present until live flags
    // load, then overrides them with whatever flagsmith has so far.
    const flags = { ...defaultFlags, ...($flagsmith?.getAllFlags() || {}) }

    Object.entries(flags).forEach(([name, data]) => {
      // attempt to parse the value as JSON if it's a string
      if (typeof data.value === 'string') {
        try {
          data.value = JSON.parse(data.value)
        } catch {
          // leave as is if parsing fails because it's not JSON
        }
      }

      // Convert flag names to camelCase and store in the reactive state
      featureFlags[camelCase(name)] = data
    })

    // Log the updated feature flags
    $logger.debug('FEATURE FLAGS UPDATED', featureFlags)

    flagsAreInitialized.value = true
  }

  // Initialize feature flags on store creation to ensure defaults are set immediately
  // Flag values will be updated asynchronously when Flagsmith loads, see /boot/flagsmith.js
  initFeatureFlags()

  return {
    initFeatureFlags,
    featureFlags,
    flagsAreInitialized,
  }
})
